import { GetAppointment } from "../../services/fhir";
import { getDependentIdentifier } from "./getDependentIdentifier";

type ConsentReceived = {
  id?: string;
  purpose?: string;
};

type FetchDependentAppointmentsParams = {
  dependents: Array<any> | undefined;
  receivedConsents: Array<ConsentReceived> | undefined;
  startDate: string;
  endDate: string;
};

/**
 * Centraliza:
 * - resolução do identificador do dependente (via `getDependentIdentifier`)
 * - verificação de consentimento ("received") para obter o `purposeofuse`
 * - busca dos appointments dos dependentes (apenas quando houver consentimento)
 *
 * Observação:
 * - Mantém o comportamento atual: dependentes sem consentimento não disparam requisição.
 * - Retorna um array único com todas as `entry` (flatten), pronto para ser mesclado no bundle.
 */
export async function fetchDependentAppointments({
  dependents,
  receivedConsents,
  startDate,
  endDate,
}: FetchDependentAppointmentsParams): Promise<any[]> {
  const safeDependents = dependents ?? [];
  const safeConsents = receivedConsents ?? [];

  const requests = safeDependents
    .map((dependente) => {
      const dependenteId = getDependentIdentifier(dependente);
      if (!dependenteId) return undefined;

      const purpose = safeConsents.find((c) => c?.id === dependenteId)?.purpose;
      if (!purpose) return undefined;

      return GetAppointment({
        ehrrunner: `${dependenteId}`,
        startDate,
        endDate,
        purposeofuse: purpose,
        status: ["booked", "pending", "proposed"],
        sort: "date",
        includeLocation: true,
      });
    })
    .filter(Boolean) as Array<ReturnType<typeof GetAppointment>>;

  const responses = await Promise.all(requests);

  return responses?.map((bundle: any) => bundle?.entry || [])?.flat();
}
